import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import IProduct from '../../../app/_interfaces/IProduct';
import ISite from '../../../app/_interfaces/ISite';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  constructor(private http: HttpClient) {}

  private get apiBaseUrl(): string {
    return `${environment.api.protocol}://${environment.api.host}:${environment.api.port}`;
  }

  private resolveCanonicalProductId(product: IProduct): number {
    return +(product?.products_id || product?.id || 0);
  }

  private toProductImageUrl(customers_id: number, productId: number, version?: string): string {
    const cacheBuster = `${version || Date.now()}`;
    return `${this.apiBaseUrl}/api/v1/public/customers/${customers_id}/products/${productId}/image?v=${encodeURIComponent(cacheBuster)}`;
  }

  private decorateProduct(customers_id: number, product: IProduct): IProduct {
    const productId = this.resolveCanonicalProductId(product);
    const rawImageUrl = `${product?.image_url || ''}`.trim();
    if (!productId || !rawImageUrl) {
      return product;
    }

    const version =
      `${product?.updatedAt || ''}`.trim() ||
      `${(product as any)?.updated_at || ''}`.trim() ||
      rawImageUrl;

    return {
      ...product,
      image_url: this.toProductImageUrl(customers_id, productId, version),
    };
  }

  private decorateProducts(customers_id: number, products: IProduct[]): IProduct[] {
    return (products || []).map((product) => this.decorateProduct(customers_id, product));
  }

  getAllByCustomer(customers_id: number) {
    return this.http.get<IProduct[]>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/products`,
      {}
    ).pipe(map((products) => this.decorateProducts(customers_id, products)));
  }

  getByCustomerAndSite(customers_id: number, sites_id: number) {
    return this.http.get<IProduct[]>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${sites_id}/products`
    ).pipe(map((products) => this.decorateProducts(customers_id, products)));
  }

  create(customers_id: number, categories_id: number, product: IProduct) {
    return this.http.post(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/categories/${categories_id}/products`,
      product
    );
  }

  update(customers_id: number, products_id: number, product: IProduct) {
    return this.http.patch(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/products/${products_id}`,
      product
    );
  }

  uploadImage(customers_id: number, products_id: number, image: File) {
    const formData = new FormData();
    formData.append('image', image);

    return this.http.patch<{
      id: number;
      image_url: string;
      file_asset_id: number;
    }>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/products/${products_id}/image`,
      formData,
    ).pipe(
      map((response) => ({
        ...response,
        image_url: this.toProductImageUrl(customers_id, +(response?.id || products_id)),
      })),
    );
  }

  delete(customers_id: number, products_id: number) {
    return this.http.delete(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/products/${products_id}`,
      {}
    );
  }

  addToSite(customers_id: number, site: ISite, product: IProduct) {
    return this.http.post(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${site.id}/products/${product.id}`,
      { price: Number(product.price || 0) },
      {}
    );
  }

  assignToAllSites(customers_id: number, products_id: number) {
    return this.http.post<{
      total: number;
      success: number;
      failed: number;
      inserted_links: number;
      updated_prices: number;
      duration_ms: number;
    }>(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/products/${products_id}/assign-all-sites`,
      {},
    );
  }

  updateOnSite(customers_id: number, site: ISite, product: IProduct) {
    return this.http.patch(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${site.id}/products/${product.id}`,
      { price: Number(product.price || 0) },
      {}
    );
  }

  deleteOnSite(customers_id: number, sites_id: number, products_id: number) {
    return this.http.delete(
      `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/customers/${customers_id}/sites/${sites_id}/products/${products_id}`,
      {}
    );
  }
}
